import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProjectDetailsDialog from "@/components/ProjectDetailsDialog";
import ProjectQrPosterSheet from "@/components/ProjectQrPosterSheet";
import { StatusBadge } from "@/components/shared";
import { SkeletonRows } from "@/components/RegisterSkeleton";
import { Plus, PencilSimple, MagnifyingGlass, Buildings, Printer } from "@phosphor-icons/react";

export default function Projects() {
  const { isWriter, isAdmin } = useAuth();
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [showPosters, setShowPosters] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/projects"); setProjects(data); } catch { /* ignore */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = projects.filter((p) => !q || JSON.stringify(p).toLowerCase().includes(q.toLowerCase()));

  const openEdit = (p) => { setEditProject(p); setDialogOpen(true); };
  const openNew = () => { setEditProject(null); setDialogOpen(true); };

  return (
    <div className="h-full flex flex-col bg-surface-page p-4">
      <div className="flex-1 min-h-0 flex flex-col bg-surface-card border border-border rounded-[12px] shadow-card overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-divider-inner shrink-0">
        <div>
          <h1 className="font-heading text-[17px] font-bold tracking-tight text-ink-primary">Projects</h1>
          <p className="text-[12px] text-ink-tertiary">All projects — click a row to edit details, forecast, stand-downs & site diary</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button onClick={() => setShowPosters(true)} data-testid="print-site-posters-btn"
              className="h-9 px-3 border border-border-control text-ink-primary font-semibold text-[12px] rounded-[7px] hover:bg-surface-panel inline-flex items-center gap-1.5">
              <Printer size={15} weight="bold" /> QR Posters
            </button>
          )}
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-tertiary" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Project, site or ref…" data-testid="projects-search"
              className="w-72 h-[42px] pl-9 pr-3 bg-white border-[1.5px] border-ink-primary rounded-[9px] text-body focus:outline-none focus:ring-2 focus:ring-equipr-yellow/35" />
          </div>
          {isWriter && (
            <button onClick={openNew} data-testid="projects-new-btn"
              className="h-[42px] px-4 bg-equipr-yellow text-equipr-black font-bold text-[12px] uppercase tracking-[0.04em] rounded-[9px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px inline-flex items-center gap-1.5">
              <Plus size={15} weight="bold" /> New Project
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <table className="w-full text-cell">
          <thead className="sticky top-0 z-10">
            <tr className="bg-surface-sunken text-left border-b border-border">
              {["Project #", "Project / Site", "Client", "Days", "Assigned", "Status", ""].map((h, i) => (
                <th key={h} className={`px-4 py-2.5 uppercase tracking-[0.1em] text-colhead ${i === 1 ? "font-bold text-ink-secondary" : "font-medium text-ink-muted"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows cols={7} />
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-ink-tertiary">{q ? "No projects match your search." : "No projects yet."}</td></tr>
            ) : filtered.map((p) => (
              <tr key={p.id} onClick={() => isWriter && openEdit(p)} data-testid={`project-row-${p.id}`}
                className="border-b border-divider-row hover:bg-accent-wash cursor-pointer">
                <td className="px-4 py-2.5 text-ink-tertiary font-mono">{p.project_no || "—"}</td>
                <td className="px-4 py-2.5">
                  <div className="font-semibold text-ink-primary flex items-center gap-1.5"><Buildings size={14} className="text-ink-tertiary" />{p.project_name || p.site_address}</div>
                  {p.project_name && <div className="text-[11px] text-ink-tertiary ml-5">{p.site_address}</div>}
                </td>
                <td className="px-4 py-2.5 text-ink-secondary">{p.client}</td>
                <td className="px-4 py-2.5 text-ink-secondary">{p.days || "—"}</td>
                <td className="px-4 py-2.5 text-ink-secondary">{(p.assignments || []).length}</td>
                <td className="px-4 py-2.5"><StatusBadge value={p.status} /></td>
                <td className="px-4 py-2.5 text-right">
                  {isWriter && <button onClick={(e) => { e.stopPropagation(); openEdit(p); }} data-testid={`project-edit-${p.id}`} className="text-ink-tertiary hover:text-ink-primary"><PencilSimple size={16} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-5 py-2 border-t border-divider-inner bg-surface-panel text-meta text-ink-tertiary shrink-0">
        <span data-testid="projects-footer">{loading ? "Loading…" : `${filtered.length} project${filtered.length === 1 ? "" : "s"}${q ? ` · filtered from ${projects.length}` : ""}`}</span>
      </div>
      </div>

      <ProjectDetailsDialog open={dialogOpen} project={editProject}
        onClose={() => setDialogOpen(false)} onSaved={() => { setDialogOpen(false); load(); }} />
      {showPosters && <ProjectQrPosterSheet onClose={() => setShowPosters(false)} />}
    </div>
  );
}
